import { useContext } from "react";
import {
  Card,
  Divider,
  Flex,
  Form,
  Input,
  Space,
  Typography,
  message,
} from "antd";
import { useNavigate, useSearchParams } from "react-router-dom";
import Button from "../../Components/Atoms/DynamicButton";
import AuthService from "../../Services/auth.service";
import { openNotification } from "../../Components/Notification/Index";
import Link from "antd/es/typography/Link";
import useOrganization from "../../Hooks/useOrganization";
import googleAnalyticService from "../../Services/google.analytic.service";
import LoaderContext from "../../Context/LoaderContext/Index";
import "./index.scss";

const Signup = () => {
  const { setLoading, setLoadingMessage, isLoading } =
    useContext(LoaderContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const authService = AuthService.getInstance();
  const { organization } = useOrganization();
  const id = searchParams.get("jdId");

  const handleSignInClick = () => {
    if (id) {
      navigate(`/sign-in?jdId=${id}`);
    } else {
      navigate("/sign-in");
    }
  };

  const handleGoogleAuthClick = async () => {
    try {
      const domain = window.location.hostname;
      await authService.handleGoogleAuth(domain);
      // Track the Google signup event with Google Analytics
      googleAnalyticService.handleSubmissionEvents({
        category: "QualifyMe Candidate Signup",
        action: "Sign Up using Google",
        label: `Candidate user signed up successfully to organization ${
          organization?.orgName || "QualifyMe"
        }`,
      });
    } catch (error) {
      console.error("Error while signing up user:", error);
      message.error("Error while signing up user. Try again!");
    }
  };

  const handleLinkedinAuthClick = async () => {
    try {
      const domain = window.location.hostname;
      await authService.handleLinkedinAuth(domain);
    } catch (error) {
      console.error("Error while signing up user:", error);
      message.error("Error while signing up user. Try again!");
    }
  };

  const handleSignUp = async (formValues: any) => {
    try {
      setLoading(true);
      setLoadingMessage("Signing up...");
      const data = { ...formValues };
      delete data.confirm;
      const response = await authService.signUpUser(data);
      if (response) {
        if (response.status === 400) {
          openNotification("error", "User already exists", "");
          return;
        }

        // Track the signup event with Google Analytics
        googleAnalyticService.handleSubmissionEvents({
          category: "QualifyMe Candidate Signup",
          action: "Sign Up using email",
          label: `Candidate user with email ${
            data.email
          } signed up successfully to organization ${
            organization?.orgName || "QualifyMe"
          }`,
        });

        const signInResponse = await authService.signIn({
          email: data.email,
          password: formValues.password,
        });

        if (signInResponse) {
          openNotification("success", "Account created successfully", "");
          if (id) {
            navigate(`/${id}/requirements`);
          } else {
            navigate("/");
          }
        }
      }
    } catch (err) {
      console.error("Error while signing up user:", err);
      message.error("Error while signing up user. Try again!");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="signup-container">
      <div className="signup-content">
        <Card
          className="signup-card"
          style={{ maxWidth: 360, width: "100%" }}
          bordered
          title={
            <Divider orientation="left">
              <Typography.Title style={{ margin: 0 }} level={4}>
                Signup
              </Typography.Title>
            </Divider>
          }
        >
          <Form
            name="normal_login"
            className="login-form"
            initialValues={{ remember: true }}
            onFinish={handleSignUp}
            autoComplete="off"
          >
            <Form.Item
              name="firstName"
              rules={[
                { required: true, message: "Please enter your first name!" },
                {
                  pattern: /^[A-Za-z]{2,50}$/,
                  message:
                    "First name must contain only letters and be 2-50 characters long!",
                },
              ]}
            >
              <Input placeholder="Firstname" />
            </Form.Item>
            <Form.Item
              name="lastName"
              rules={[
                { required: true, message: "Please enter your last name!" },
                {
                  pattern: /^[A-Za-z]{2,50}$/,
                  message:
                    "Last name must contain only letters and be 2-50 characters long!",
                },
              ]}
            >
              <Input placeholder="Lastname" />
            </Form.Item>
            <Form.Item
              name="email"
              rules={[
                {
                  type: "email",
                  message: "The input is not valid E-mail!",
                },
                {
                  required: true,
                  message: "Please enter your email!",
                },
                {
                  pattern: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                  message: "Please enter a valid email address!",
                },
              ]}
            >
              <Input placeholder="Email" />
            </Form.Item>

            <Form.Item
              name="password"
              rules={[
                {
                  required: true,
                  message: "Please enter your password!",
                },
                {
                  min: 8,
                  message: "Password must be at least 8 characters long!",
                },
                {
                  pattern: /[A-Z]/,
                  message:
                    "Password must contain at least one uppercase letter!",
                },
                {
                  pattern: /[a-z]/,
                  message:
                    "Password must contain at least one lowercase letter!",
                },
                {
                  pattern: /[0-9]/,
                  message: "Password must contain at least one number!",
                },
                {
                  pattern: /[!@#$%^&*(),.?":{}|<>]/,
                  message:
                    "Password must contain at least one special character!",
                },
              ]}
            >
              <Input.Password placeholder="Password" />
            </Form.Item>
            <Form.Item
              name="confirm"
              dependencies={["password"]}
              rules={[
                {
                  required: true,
                  message: "Please confirm your password!",
                },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("password") === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error(
                        "The new password that you entered do not match!"
                      )
                    );
                  },
                }),
              ]}
            >
              <Input.Password placeholder="Confirm Password" />
            </Form.Item>
            <Space size={16} direction="vertical" style={{ width: "100%" }}>
              <Button
                type="primary"
                htmlType="submit"
                block
                loading={isLoading}
              >
                {isLoading ? "" : "Signup"}
              </Button>
              <Flex gap={10} vertical={true}>
                <Button
                  icon={
                    <img
                      src="/assets/google.svg"
                      alt="Google"
                      style={{ width: 20 }}
                    />
                  }
                  onClick={handleGoogleAuthClick}
                  style={{ width: "100%" }}
                >
                  Sign up with Google
                </Button>
                <Button
                  icon={
                    <img
                      src="/assets/linkedin.svg"
                      alt="Google"
                      style={{ width: 20 }}
                    />
                  }
                  style={{ width: "100%" }}
                >
                  Sign up with LinkedIn
                </Button>
              </Flex>
              <Typography.Paragraph style={{ textAlign: "center" }}>
                Already have an account?{" "}
                <Button
                  onClick={handleSignInClick}
                  type="link"
                  loading={isLoading}
                >
                  Sign In
                </Button>
              </Typography.Paragraph>
            </Space>
            <Flex
              style={{
                color: "#9c9a97",
                marginTop: "25px",
                marginBottom: "3px",
              }}
              gap={"0px 8px"}
            >
              <Space direction="horizontal">
                <Link
                  href="https://www.qualifyme.ai/terms"
                  style={{ color: "#9c9a97", fontSize: "14px" }}
                >
                  Terms and Conditions
                </Link>
              </Space>
              <Typography.Text style={{ color: "#9c9a97", fontSize: "13px" }}>
                |
              </Typography.Text>
              <Space direction="horizontal">
                <Link
                  href="https://www.qualifyme.ai/privacy"
                  style={{ color: "#9c9a97", fontSize: "14px" }}
                >
                  Privacy Policy
                </Link>
              </Space>
            </Flex>
          </Form>
        </Card>
      </div>
    </div>
  );
};

export default Signup;
